import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue } from 'jotai';
import { useContext } from 'react';

import { CompetitionStatus, CompetitionStatusEnum } from '@flipster/apis/v2/trading-competition/types';
import { NavigationHeightVariableStyleValue } from '@flipster/react/panda/globals/vars';
import { css } from '@flipster/styled-system/css';
import { center, hstack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { CompetitionsEvent } from '@flipster/utils/segment/events/competitions';

import { ImgOrLottie, contentfulImageUrl } from '../../../contentful/assets';
import { CompetitionIdContext, EventTabValue, competitionApiBunja, competitionTabsBunja } from '../state';

const competitionStatusToLoggingStatus = (status: CompetitionStatus): string => {
  switch (status) {
    case CompetitionStatusEnum.Upcoming:
      return 'BEFORE';
    case CompetitionStatusEnum.Ongoing:
      return 'DURING';
    case CompetitionStatusEnum.Finished:
      return 'AFTER';
    default:
      return status;
  }
};

const updateTabQueryParam = (tabValue: string) => {
  const url = new URL(window.location.href);
  url.searchParams.set('tab', tabValue);
  window.history.replaceState(null, '', url.toString());
};

export const EventTab = () => {
  const id = useContext(CompetitionIdContext);
  const { eventTabs, selectedEventTabValueAtom } = useBunja(competitionTabsBunja);
  const { competitionStatusAtom } = useBunja(competitionApiBunja);
  const [selectedEventTabValue, setSelectedEventTabValue] = useAtom(selectedEventTabValueAtom);
  const competitionStatus = useAtomValue(competitionStatusAtom);

  if (!eventTabs || eventTabs.length <= 1) return null;

  return (
    <div
      className={css({
        position: 'sticky',
        zIndex: 20,
        w: '100%',
        px: { base: 16, tablet: 24 },
        py: { base: 16, tablet: 24 },
        rounded: { base: 12, tablet: 28 },
        bgColor: 'background.default.level-0',
      })}
      style={{ top: `calc(${NavigationHeightVariableStyleValue} - 1px)` }}
    >
      <ul
        className={hstack({
          w: '100%',
          gap: { base: 12, tablet: 20 },
          overflowX: 'scroll',
        })}
      >
        {eventTabs.map((item, index) => {
          const isSelected = selectedEventTabValue === item.value;
          return (
            <li
              key={item.value}
              className={css({ tabletDown: { flexShrink: 0, w: 'fit-content', maxW: 250 }, tablet: { flex: 1 } })}
            >
              <button
                type='button'
                className={css(
                  center.raw({
                    w: '100%',
                    gap: 8,
                    px: { base: 16, tablet: 24 },
                    py: 16,
                    textStyle: 'Title/S',
                    textAlign: 'left',
                    color: 'content.default.level-5',
                    border: '1px solid transparent',
                    rounded: 16,
                  }),
                  !isSelected && {
                    borderColor: { tabletDown: 'border.default.level-1' },
                    _hover: {
                      bg: 'rgba(var(--key-color-rgb), 0.1)',
                    },
                  },
                )}
                style={
                  isSelected
                    ? {
                        // 내부 배경 위에 옅은 키 컬러 그라데이션(오버레이) + 외곽 보더 그라데이션 유지
                        background:
                          'radial-gradient(ellipse at bottom, color-mix(in srgb, var(--key-color) 10%, transparent) 3%, transparent 40%), linear-gradient(0deg, #111111, #111111), linear-gradient(120deg, #B4B4B4 1.89%, var(--key-color) 60%, var(--key-color) 99.78%)',
                        backgroundClip: 'padding-box, padding-box, border-box',
                        backgroundOrigin: 'border-box',
                      }
                    : undefined
                }
                onClick={() => {
                  if (item.value) {
                    segment.track(CompetitionsEvent.CompetitionTradingEventTabClick, {
                      competition_id: id,
                      list_order: index,
                      competition_status: competitionStatusToLoggingStatus(competitionStatus),
                    });
                    setSelectedEventTabValue(item.value as EventTabValue);
                    updateTabQueryParam(item.value);
                    const competitionContent = document.getElementById('competition-content');
                    if (competitionContent) {
                      const offset = 136; // eventTab 높이만큼 상단 여백
                      const y = competitionContent.getBoundingClientRect().top + window.pageYOffset - offset;
                      window.scrollTo({ top: y, behavior: 'smooth' });
                    }
                  }
                }}
              >
                <ImgOrLottie
                  url={contentfulImageUrl(item.icon?.url ?? '')}
                  alt={item.label ?? ''}
                  css={{ w: { base: 24, tablet: 50 }, h: { base: 24, tablet: 50 } }}
                />
                <span
                  className={css({
                    flex: 1,
                    minW: 0,
                    display: '-webkit-box',
                    lineClamp: 2,
                    boxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    wordBreak: 'break-word',
                  })}
                >
                  {item.label}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
